import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Grid, Text, VStack, Spinner, useT, Modal, ConfirmDialog, toaster } from '@minetrace/ui'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  // LuArrowLeft removed - back button removed
  // LuMapPin removed
  // LuCircleCheck removed - no longer used in component check cards
  // LuMessageSquare removed - no longer used in observations header
  // LuClock removed
  // LuUsers removed - geologists inline now
  LuCamera as Camera,
  // LuFileText removed - no longer used in evidence chips
  LuTriangleAlert as AlertTriangle,
  LuPause as Pause,
  LuPlay as Play,
  LuFlag as Flag,
  LuLocate as Navigation,
  LuDownload as Download,
  LuCircleCheck as CircleCheck,
  LuCircleX as CircleX,
  // LuShare2 removed
  // LuCalendar removed
  LuMountain as Mountain,
  LuSettings as Settings,
  LuRuler as Ruler,
  LuClock as Clock,
} from 'react-icons/lu'
import { KMap, AdvancedMarker, Polyline, FitBounds, MarkerTooltip, useMap } from '../../lib/googleMaps'
import { ComponentPin, MapPin } from '../../lib/mapMarkers'
import { programacionesService } from '../../api/services'
import { apiClient } from '../../api/client'
import type {
  VisitaDetailResponse,
  VisitaComponentCheckItem,
  VisitaObservationItem,
  VisitaStateLogItem,
  VisitaOcdeAuditResult,
  VisitaOcdeCategoryItem,
  VisitaOcdeQuestionItem,
} from '../../api/models/response/visita'
import { brand, themeColors } from '../../theme'
import { DETAIL_STATUSES } from '../../data/enums'
import { latLngToUtm } from '../../utils/utmConverter'

const MotionBox = motion.create(Box)

// ---------------------------------------------------------------------------
// Design palette (mapped from spec)
// ---------------------------------------------------------------------------
const P = {
  primaryContainer: '#b4532a',
  primary: '#012d1d',
  onPrimary: '#ffffff',
  secondary: '#51634d',
  surface: '#f7faf8',
  surfaceContainerLow: '#f1f4f2',
  surfaceContainerLowest: '#ffffff',
  outlineVariant: '#c1c8c2',
  onSurface: '#2a2622',
  onSurfaceVariant: '#414844',
  primaryFixed: '#c1ecd4',
  onPrimaryFixedVariant: '#274e3d',
} as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const colors = themeColors

function formatDatetime(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    return d.toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00')
    return d.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

// Kept for potential reuse
// function formatUtm(lat: number | null, lng: number | null): string {
//   if (lat == null || lng == null) return '-'
//   const { northing, easting } = latLngToUtm(lat, lng, 18)
//   return `N ${northing.toLocaleString('en')} / E ${easting.toLocaleString('en')}`
// }

function getInitials(name: string | null): string {
  return (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'
}

// Schedule status colors available for future use
// const scheduleStatusColors: Record<number, { bg: string; color: string }> = {
//   0: { bg: 'rgba(212, 154, 42, 0.2)', color: '#d49a2a' },
//   1: { bg: 'rgba(61, 111, 168, 0.2)', color: '#3d6fa8' },
//   2: { bg: 'rgba(42, 138, 135, 0.2)', color: '#2a8a87' },
//   3: { bg: 'rgba(42, 138, 135, 0.2)', color: '#2a8a87' },
//   4: { bg: 'rgba(107, 114, 128, 0.2)', color: '#857b71' },
// }

const detailStatusColors: Record<number, { bg: string; color: string }> = {
  1: { bg: 'rgba(61, 111, 168, 0.2)', color: '#3d6fa8' },
  2: { bg: 'rgba(212, 154, 42, 0.2)', color: '#d49a2a' },
  3: { bg: 'rgba(42, 138, 135, 0.2)', color: '#2a8a87' },
  4: { bg: 'rgba(107, 114, 128, 0.2)', color: '#857b71' },
  5: { bg: 'rgba(200, 68, 47, 0.2)', color: '#c8442f' },
}

function checkStatusInfo(status: number): { labelTid: string; bg: string; color: string } {
  if (status === 1)
    return { labelTid: 'programaciones.visita.statusVerified', bg: P.primaryFixed, color: P.onPrimaryFixedVariant }
  if (status === 2)
    return { labelTid: 'programaciones.visita.statusNotFound', bg: 'rgba(200, 68, 47,0.12)', color: '#b23a26' }
  if (status === 3)
    return { labelTid: 'programaciones.visita.statusObserved', bg: '#f9eccf', color: '#92400E' }
  return { labelTid: 'programaciones.visita.statusUnknown', bg: 'rgba(181, 170, 157,0.15)', color: '#857b71' }
}

// Kept for potential reuse
// function checkStatusStyle(status: number): { bg: string; color: string } {
//   if (status === 1) return { bg: 'rgba(42, 138, 135, 0.15)', color: '#2a8a87' }
//   if (status === 2) return { bg: 'rgba(200, 68, 47, 0.15)', color: '#c8442f' }
//   return { bg: 'rgba(181, 170, 157, 0.15)', color: '#857b71' }
// }

function detailStatusLabel(status: number): string {
  return DETAIL_STATUSES.find((s) => s.value === status)?.label ?? 'Desconocido'
}

// Note: 'Desconocido' fallback above is a data-enum label, not user copy

function stateLogIcon(action: string | null) {
  switch (action?.toLowerCase()) {
    case 'iniciar':
      return <Play size={13} />
    case 'pausar':
      return <Pause size={13} />
    case 'reanudar':
      return <Play size={13} />
    case 'finalizar':
      return <Flag size={13} />
    default:
      return <Clock size={13} />
  }
}

function stateLogColor(action: string | null): string {
  switch (action?.toLowerCase()) {
    case 'iniciar':
      return P.primaryContainer
    case 'pausar':
      return '#a8740e'
    case 'reanudar':
      return '#1d6664'
    case 'finalizar':
      return '#857b71'
    default:
      return '#b5aa9d'
  }
}

// ---------------------------------------------------------------------------
// Map helpers
// ---------------------------------------------------------------------------
type LL = google.maps.LatLngLiteral

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base no exporta uno).
 * Mismo patrón de ciclo de vida que `lib/googleMaps/Polyline`.
 */
/**
 * Pin de "aquí se tomó una foto". Violeta para no chocar con el azul de los
 * componentes declarados ni con el verde/rojo de los verificados. El pin
 * aproximado (evidencia de componente, que hereda la coordenada de la
 * verificación) va traslúcido: la diferencia debe verse en el mapa, no sólo
 * leerse en el tooltip.
 */
function PhotoPin({ exact }: { exact: boolean }) {
  return (
    <Box opacity={exact ? 1 : 0.6}>
      <MapPin
        color="#7C3AED"
        size={32}
        icon={<Camera size={13} color="white" strokeWidth={2.5} />}
      />
    </Box>
  )
}

/**
 * Ray casting sobre el anillo exterior del polígono declarado. A esta escala
 * (un derecho minero, pocos km) tratar lat/lng como plano no introduce error
 * apreciable, y evita depender de la geometry library de Google Maps.
 */
function isPointInPolygon(point: LL, ring: LL[]): boolean {
  if (ring.length < 3) return false
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i]!.lat
    const xi = ring[i]!.lng
    const yj = ring[j]!.lat
    const xj = ring[j]!.lng
    const intersects =
      yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi
    if (intersects) inside = !inside
  }
  return inside
}

function MapPolygon({
  paths,
  strokeColor,
  fillColor,
  fillOpacity = 0.12,
  strokeWeight = 2,
}: {
  paths: LL[]
  strokeColor: string
  fillColor?: string
  fillOpacity?: number
  strokeWeight?: number
}) {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])

  useEffect(() => {
    polyRef.current?.setOptions({
      paths,
      strokeColor,
      strokeWeight,
      fillColor: fillColor ?? strokeColor,
      fillOpacity,
      clickable: false,
    })
  }, [paths, strokeColor, strokeWeight, fillColor, fillOpacity])

  return null
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function VisitaDetallePage() {
  const navigate = useNavigate()
  const { t } = useT()
  const { id: scheduleId, detailId } = useParams()
  const [data, setData] = useState<VisitaDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Review state (approve/reject the completed visit)
  const [reviewMode, setReviewMode] = useState<'approve' | 'reject' | null>(null)
  const [reviewComment, setReviewComment] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submittingReview, setSubmittingReview] = useState(false)

  useEffect(() => {
    if (!scheduleId || !detailId) return
    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await programacionesService.getVisitaDetail(scheduleId, detailId)
        if (!cancelled) setData(res)
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('programaciones.visita.errorDefault'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => {
      cancelled = true
    }
  }, [scheduleId, detailId, t])

  const refreshDetail = async () => {
    if (!scheduleId || !detailId) return
    try {
      const res = await programacionesService.getVisitaDetail(scheduleId, detailId)
      setData(res)
    } catch {
      /* ignore */
    }
  }

  const openReview = (mode: 'approve' | 'reject') => {
    setReviewMode(mode)
    setReviewComment('')
    setConfirmOpen(false)
  }

  const closeReview = () => {
    if (submittingReview) return
    setReviewMode(null)
    setReviewComment('')
    setConfirmOpen(false)
  }

  const handleAskConfirm = () => {
    if (!reviewComment.trim()) {
      toaster.error({ description: 'El comentario es obligatorio.' })
      return
    }
    setConfirmOpen(true)
  }

  const handleSubmitReview = async () => {
    if (!scheduleId || !detailId || !reviewMode) return
    setSubmittingReview(true)
    try {
      if (reviewMode === 'approve') {
        await programacionesService.aprobarVisita(scheduleId, detailId, reviewComment.trim())
        toaster.success({ description: 'Visita aprobada correctamente.' })
      } else {
        await programacionesService.rechazarVisita(scheduleId, detailId, reviewComment.trim())
        toaster.success({ description: 'Visita rechazada correctamente.' })
      }
      setReviewMode(null)
      setReviewComment('')
      setConfirmOpen(false)
      await refreshDetail()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'No se pudo completar la operación.'
      toaster.error({ description: message })
    } finally {
      setSubmittingReview(false)
    }
  }

  const openAuthFile = async (path: string) => {
    // fetchAuthorized renueva el token en 401; el fetch a mano fallaba con la sesión vencida.
    const res = await apiClient.fetchAuthorized(`${import.meta.env.VITE_API_BASE_URL}/programaciones/files/${path}`)
    if (res.ok) {
      const blob = await res.blob()
      window.open(URL.createObjectURL(blob), '_blank')
    }
  }

  // ── Loading ──
  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" minH="400px" direction="column" gap={4}>
        <Spinner size="xl" color={brand.green} />
        <Text tid="programaciones.visita.loading" color={colors.textMuted} fontFamily="'Instrument Sans', sans-serif" />
      </Flex>
    )
  }

  // ── Error ──
  if (error || !data) {
    return (
      <Flex justifyContent="center" alignItems="center" minH="400px" direction="column" gap={4}>
        <AlertTriangle size={40} color="#c8442f" />
        <Text color={colors.text} fontWeight="600">
          {error ?? t('programaciones.visita.notFound')}
        </Text>
        <Box
          as="button"
          px={4}
          py={2}
          bg={brand.green}
          color="white"
          borderRadius="lg"
          fontSize="sm"
          fontWeight="600"
          cursor="pointer"
          onClick={() => navigate(-1)}
        >
          {t('programaciones.visita.back')}
        </Box>
      </Flex>
    )
  }

  // ── Parse polygon ──
  const polygonPositions: LL[] = []
  if (data.boundaryGeoJson) {
    try {
      const geo = JSON.parse(data.boundaryGeoJson)
      const coords =
        geo.type === 'Polygon'
          ? geo.coordinates[0]
          : geo.type === 'MultiPolygon'
            ? geo.coordinates[0][0]
            : []
      for (const c of coords) {
        polygonPositions.push({ lat: c[1], lng: c[0] })
      }
    } catch {
      /* ignore */
    }
  }

  const trackPositions: LL[] = (data.locationPoints ?? []).map(
    (p) => ({ lat: p.latitude, lng: p.longitude }),
  )

  const declaredMarkers: { pos: LL; name: string; type: string; code: string | null }[] = []
  const capturedMarkers: { pos: LL; name: string; type: string; code: string | null; status: number }[] = []
  for (const check of data.componentChecks ?? []) {
    const checkType = check.componentTypeName ?? t('programaciones.visita.componentDefault')
    if (check.declaredLatitude != null && check.declaredLongitude != null) {
      declaredMarkers.push({ pos: { lat: check.declaredLatitude, lng: check.declaredLongitude }, name: check.componentName ?? t('programaciones.visita.componentDefault'), type: checkType, code: check.componentTypeCode })
    }
    if (check.capturedLatitude != null && check.capturedLongitude != null) {
      capturedMarkers.push({ pos: { lat: check.capturedLatitude, lng: check.capturedLongitude }, name: check.componentName ?? t('programaciones.visita.componentDefault'), type: checkType, code: check.componentTypeCode, status: check.checkStatus })
    }
  }

  // ── Dónde se tomó cada foto ──
  // Tres orígenes distintos, con distinta precisión:
  //  • Observaciones y encuesta OCDE guardan la coordenada de la propia foto.
  //  • Las evidencias de componente no la guardan (FieldVisitEvidence no tiene
  //    lat/lng), así que se ubican en el punto donde el geólogo verificó ese
  //    componente. `exact` distingue ambos casos para no presentar una
  //    aproximación como si fuera una medición.
  type PhotoMarker = {
    pos: LL
    origin: string
    label: string
    exact: boolean
    capturedAt?: string | null
  }
  const photoMarkers: PhotoMarker[] = []

  for (const check of data.componentChecks ?? []) {
    if (check.capturedLatitude == null || check.capturedLongitude == null) continue
    const pos = { lat: check.capturedLatitude, lng: check.capturedLongitude }
    for (const ev of check.evidences ?? []) {
      if (!ev.storagePath) continue
      photoMarkers.push({
        pos,
        origin: t('programaciones.visita.photoOriginEvidence'),
        label: ev.description || ev.fileName || (check.componentName ?? ''),
        exact: false,
        capturedAt: ev.capturedAt,
      })
    }
  }

  for (const obs of data.observations ?? []) {
    if (!obs.photoStoragePath || obs.latitude == null || obs.longitude == null) continue
    photoMarkers.push({
      pos: { lat: obs.latitude, lng: obs.longitude },
      origin: t('programaciones.visita.photoOriginObservation'),
      label: obs.observationReasonName || obs.comments || '',
      exact: true,
      capturedAt: obs.mobileTimestamp ?? obs.createdAt,
    })
  }

  for (const cat of data.ocdeAudit?.categories ?? []) {
    for (const q of cat.questions ?? []) {
      if (!q.photoStoragePath || q.photoLatitude == null || q.photoLongitude == null) continue
      photoMarkers.push({
        pos: { lat: q.photoLatitude, lng: q.photoLongitude },
        origin: t('programaciones.visita.photoOriginOcde'),
        label: `${q.code} — ${q.text}`,
        exact: true,
        capturedAt: q.photoCapturedAt ?? q.mobileTimestamp,
      })
    }
  }

  // Cuántas de esas fotos cayeron dentro del polígono declarado. Es la lectura
  // que interesa al auditor: si todas están dentro, la evidencia es coherente
  // con el derecho minero; si alguna está fuera, el mapa lo muestra.
  const photosInsidePolygon = polygonPositions.length >= 3
    ? photoMarkers.filter((m) => isPointInPolygon(m.pos, polygonPositions)).length
    : null

  const allPositions: LL[] = [
    ...polygonPositions,
    ...trackPositions,
    ...declaredMarkers.map((m) => m.pos),
    ...capturedMarkers.map((m) => m.pos),
    ...photoMarkers.map((m) => m.pos),
  ]

  const detailStatusLbl = detailStatusLabel(data.detailStatus)
  const detStatusStyle = detailStatusColors[data.detailStatus] ?? { bg: 'rgba(181, 170, 157,0.2)', color: '#857b71' }

  // representativePos removed - coordinates overlay removed from map

  return (
    <VStack gap={0} alignItems="stretch" pb={12}>

      {/* ── Page Header ── */}
      <MotionBox
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        px={{ base: 4, md: 8 }}
        py={6}
      >
        {/* Badge + code row */}
        <Flex alignItems="center" gap={3} mb={3}>
          {/* Detail status pill only */}
          <Box
            px={3}
            py="3px"
            borderRadius="full"
            fontSize="10px"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.08em"
            bg={detStatusStyle.bg}
            color={detStatusStyle.color}
            fontFamily="'Instrument Sans', sans-serif"
          >
            {detailStatusLbl}
          </Box>

        </Flex>

        {/* Title + actions */}
        <Flex
          alignItems={{ base: 'flex-start', md: 'flex-end' }}
          justifyContent="space-between"
          gap={4}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <Box>
            <Text
              tid="programaciones.visita.title"
              as="h1"
              fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
              fontWeight="800"
              letterSpacing="-0.03em"
              color={P.primary}
              fontFamily="Bricolage Grotesque, sans-serif"
              lineHeight={1.1}
              mb={1}
            />
            <Text
              fontSize="xl"
              color={P.secondary}
              fontWeight="500"
              fontFamily="'Instrument Sans', sans-serif"
            >
              {data.companyName ?? t('programaciones.visita.dash')}
              {data.companyRuc ? (
                <Box as="span">{t('programaciones.visita.rucInline', { ruc: data.companyRuc })}</Box>
              ) : null}
            </Text>
          </Box>

          {/* Action buttons */}
          <Flex gap={3} flexShrink={0} flexWrap="wrap">
            {data.detailStatus >= 4 && (
              <Box
                as="button"
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={8}
                py={3}
                borderRadius="xl"
                bg={P.primary}
                color={P.onPrimary}
                fontFamily="'Instrument Sans', sans-serif"
                fontSize="sm"
                fontWeight="700"
                cursor="pointer"
                _hover={{ bg: P.primaryContainer }}
                style={{
                  boxShadow: '0 4px 14px rgba(1,45,29,0.15)',
                  transition: 'all 0.15s',
                }}
                onClick={async () => {
                  const token = localStorage.getItem('auth_token')
                  const res = await fetch(
                    `${import.meta.env.VITE_API_BASE_URL}/programaciones/${scheduleId}/details/${detailId}/acta-pdf`,
                    { headers: { Authorization: `Bearer ${token}` } },
                  )
                  if (res.ok) {
                    const blob = await res.blob()
                    window.open(URL.createObjectURL(blob), '_blank')
                  }
                }}
              >
                <Download size={15} />
                {t('programaciones.visita.downloadActaPdf')}
              </Box>
            )}

            {/* Review actions — when the visit is Completado (legacy) or Finalizado (post-acta) pending review */}
            {(data.detailStatus === 3 || data.detailStatus === 4) && (
              <>
                <Box
                  as="button"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={6}
                  py={3}
                  borderRadius="xl"
                  bg="#1d6664"
                  color="white"
                  fontFamily="'Instrument Sans', sans-serif"
                  fontSize="sm"
                  fontWeight="700"
                  cursor="pointer"
                  _hover={{ bg: '#174f4d' }}
                  style={{
                    boxShadow: '0 4px 14px rgba(21,128,61,0.20)',
                    transition: 'all 0.15s',
                  }}
                  onClick={() => openReview('approve')}
                >
                  <CircleCheck size={16} />
                  Aprobar visita
                </Box>
                <Box
                  as="button"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={6}
                  py={3}
                  borderRadius="xl"
                  bg="white"
                  color="#9a3020"
                  border="1px solid"
                  borderColor="#fecaca"
                  fontFamily="'Instrument Sans', sans-serif"
                  fontSize="sm"
                  fontWeight="700"
                  cursor="pointer"
                  _hover={{ bg: '#fcefeb' }}
                  style={{ transition: 'all 0.15s' }}
                  onClick={() => openReview('reject')}
                >
                  <CircleX size={16} />
                  Rechazar visita
                </Box>
              </>
            )}
          </Flex>
        </Flex>

        {/* Review summary banner — shown after a decision is made */}
        {(data.detailStatus === 6 || data.detailStatus === 7) && (
          <Box
            mt={4}
            p={4}
            borderRadius="xl"
            bg={data.detailStatus === 6 ? 'rgba(42, 138, 135,0.08)' : 'rgba(200, 68, 47,0.08)'}
            border="1px solid"
            borderColor={data.detailStatus === 6 ? 'rgba(42, 138, 135,0.25)' : 'rgba(200, 68, 47,0.25)'}
          >
            <Flex alignItems="center" gap={2} mb={1}>
              {data.detailStatus === 6 ? (
                <CircleCheck size={16} color="#1d6664" />
              ) : (
                <CircleX size={16} color="#9a3020" />
              )}
              <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color={data.detailStatus === 6 ? '#1d6664' : '#9a3020'}>
                {data.detailStatus === 6 ? 'Visita aprobada' : 'Visita rechazada'}
                {data.reviewedAt ? ` · ${formatDatetime(data.reviewedAt)}` : ''}
              </Text>
            </Flex>
            {data.reviewComment && (
              <Text fontSize="sm" color="#4f4842" lineHeight={1.5}>
                {data.reviewComment}
              </Text>
            )}
          </Box>
        )}
      </MotionBox>

      {/* ── Main content ── */}
      <Box px={{ base: 4, md: 8 }} pt={7}>

        {/* ── Bento Grid: 3 column layout ── */}
        <Grid
          templateColumns={{ base: '1fr', lg: '5fr 3fr 4fr' }}
          gap={5}
          mb={5}
        >
            {/* ── Col 1: Highlighted card: Derecho Minero ── */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
              bg={P.primaryContainer}
              borderRadius="2xl"
              p={7}
              position="relative"
              overflow="hidden"
            >
              {/* Background texture dot */}
              <Box
                position="absolute"
                top="-30px"
                right="-30px"
                w="140px"
                h="140px"
                borderRadius="full"
                bg="rgba(255,255,255,0.04)"
                pointerEvents="none"
              />
              <Box
                position="absolute"
                bottom="-20px"
                left="40%"
                w="100px"
                h="100px"
                borderRadius="full"
                bg="rgba(255,255,255,0.03)"
                pointerEvents="none"
              />

              <Flex alignItems="center" gap={2} mb={3}>
                <Mountain size={14} color="rgba(255,255,255,0.6)" />
                <Text
                  tid="programaciones.visita.miningRightLabel"
                  fontSize="10px"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.12em"
                  color="rgba(255,255,255,0.6)"
                  fontFamily="'Instrument Sans', sans-serif"
                />
              </Flex>

              <Text
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="700"
                color={P.onPrimary}
                fontFamily="Bricolage Grotesque, sans-serif"
                lineHeight={1.15}
                mb={2}
              >
                {data.miningRightName ?? t('programaciones.visita.noName')}
              </Text>

              <Flex gap={4} flexWrap="wrap">
                {data.miningRightCode && (
                  <Box>
                    <Text tid="programaciones.visita.codeLabel" fontSize="10px" color="rgba(255,255,255,0.5)" fontWeight="600" textTransform="uppercase" letterSpacing="0.1em" fontFamily="'Instrument Sans', sans-serif" mb="2px" />
                    <Text fontSize="sm" color="rgba(255,255,255,0.9)" fontFamily="monospace" fontWeight="600">
                      {data.miningRightCode}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text tid="programaciones.visita.utmZoneLabel" fontSize="10px" color="rgba(255,255,255,0.5)" fontWeight="600" textTransform="uppercase" letterSpacing="0.1em" fontFamily="'Instrument Sans', sans-serif" mb="2px" />
                  <Text tid="programaciones.visita.utmZoneValue" fontSize="sm" color="rgba(255,255,255,0.9)" fontFamily="monospace" fontWeight="600" />
                </Box>
              </Flex>
            </MotionBox>

            {/* ── Col 2: Programacion + Geólogos stacked ── */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 }}
              bg={P.surfaceContainerLow}
              borderRadius="2xl"
              p={6}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              {/* Programacion info */}
              <Box>
                <Text tid="programaciones.visita.programacionLabel" fontSize="10px" fontWeight="700" color={P.secondary} textTransform="uppercase" letterSpacing="0.15em" mb={4} />
                <VStack gap={4} alignItems="stretch">
                  <Box>
                    <Text tid="programaciones.visita.startDateLabel" fontSize="xs" color={P.onSurfaceVariant} />
                    <Text fontSize="md" fontWeight="600" color={P.onSurface} fontStyle="italic">
                      {formatDate(data.startDate)}
                    </Text>
                  </Box>
                  <Box>
                    <Text tid="programaciones.visita.endDateLabel" fontSize="xs" color={P.onSurfaceVariant} />
                    <Text fontSize="md" fontWeight="600" color={P.onSurface} fontStyle="italic">
                      {formatDate(data.endDate)}
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {/* Separator + Geologist */}
              <Box mt={5} pt={4} borderTop="1px solid" borderColor={`${P.outlineVariant}30`}>
                <Flex alignItems="center" gap={3}>
                  {data.geologists?.[0] && (
                    <>
                      <Flex
                        w="40px"
                        h="40px"
                        borderRadius="full"
                        bg={P.surfaceContainer}
                        overflow="hidden"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Text fontSize="13px" fontWeight="800" color={P.primary} fontFamily="Bricolage Grotesque, sans-serif">
                          {getInitials(data.geologists[0].fullName)}
                        </Text>
                      </Flex>
                      <Box>
                        <Text tid="programaciones.visita.geologistResponsible" fontSize="10px" fontWeight="700" color={P.secondary} textTransform="uppercase" letterSpacing="0.06em" />
                        <Text fontSize="sm" fontWeight="700" color={P.primary}>
                          {data.geologists[0].fullName}
                        </Text>
                      </Box>
                    </>
                  )}
                </Flex>
              </Box>
            </MotionBox>

          {/* ── Col 3: Timeline ── */}
          <MotionBox
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, delay: 0.12 }}
            bg={P.surfaceContainerLowest}
            borderRadius="2xl"
            p={6}
            border="1px solid"
            borderColor={`${P.outlineVariant}20`}
          >
            <Text tid="programaciones.visita.timelineTitle" fontSize="sm" fontWeight="700" color={P.primary} textTransform="uppercase" letterSpacing="0.15em" mb={6} />

            {(!data.stateLogs || data.stateLogs.length === 0) ? (
              <Text tid="programaciones.visita.noStateLogs" fontSize="xs" color={P.onSurfaceVariant} />
            ) : (
              <Box
                position="relative"
                _before={{
                  content: '""',
                  position: 'absolute',
                  left: '11px',
                  top: '8px',
                  bottom: '8px',
                  width: '2px',
                  bg: P.surfaceContainer,
                }}
              >
                <VStack gap={6} alignItems="stretch">
                  {data.stateLogs.map((log, i) => {
                    const isFirst = i === 0
                    const actionLabel = log.actionType === 'iniciar' ? t('programaciones.visita.actionStart')
                      : log.actionType === 'pausar' ? t('programaciones.visita.actionPause')
                      : log.actionType === 'reanudar' ? t('programaciones.visita.actionResume')
                      : log.actionType === 'finalizar' ? t('programaciones.visita.actionFinalize')
                      : log.actionType ?? t('programaciones.visita.actionDefault')
                    const logDate = new Date(log.mobileTimestamp ?? log.createdAt)
                    const dateStr = logDate.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }).toUpperCase()
                    const timeStr = logDate.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()

                    return (
                      <Flex key={i} position="relative" pl={8} flexDirection="column" gap="2px" opacity={isFirst ? 1 : 0.6}>
                        {/* Dot */}
                        <Flex
                          position="absolute"
                          left={0}
                          top="4px"
                          w={isFirst ? '24px' : '24px'}
                          h={isFirst ? '24px' : '24px'}
                          borderRadius="full"
                          bg={isFirst ? P.primaryFixed : P.surfaceContainer}
                          alignItems="center"
                          justifyContent="center"
                          zIndex={1}
                        >
                          <Box
                            w={isFirst ? '8px' : '6px'}
                            h={isFirst ? '8px' : '6px'}
                            borderRadius="full"
                            bg={isFirst ? P.primary : P.outlineVariant}
                          />
                        </Flex>
                        {/* Content */}
                        <Text fontSize="10px" fontWeight="700" color={P.secondary} letterSpacing="0.03em">
                          {dateStr} | {timeStr}
                        </Text>
                        <Text fontSize="sm" fontWeight={isFirst ? '700' : '500'} color={isFirst ? P.primary : P.onSurface}>
                          {actionLabel}
                        </Text>
                      </Flex>
                    )
                  })}
                </VStack>
              </Box>
            )}
          </MotionBox>
        </Grid>

        {/* ── Map Section (no card border, just map) ── */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.18 }}
          borderRadius="2xl"
          overflow="hidden"
          mb={5}
          position="relative"
          h={{ base: '350px', md: '450px' }}
          border="1px solid"
          borderColor={`${P.outlineVariant}20`}
        >
          <KMap defaultCenter={{ lat: -12.0464, lng: -77.0428 }} defaultZoom={6} mapTypeId="hybrid">
            {polygonPositions.length >= 3 && (
              <MapPolygon
                paths={polygonPositions}
                strokeColor={P.primaryContainer}
                fillColor={P.primaryContainer}
                fillOpacity={0.12}
                strokeWeight={2}
              />
            )}
            {trackPositions.length >= 2 && (
              <Polyline
                path={trackPositions}
                strokeColor="#a8740e"
                strokeWeight={3}
                strokeOpacity={0.85}
              />
            )}
            {declaredMarkers.map((m, i) => (
              <AdvancedMarker key={`d-${i}`} position={m.pos}>
                <MarkerTooltip title={m.type} subtitle={m.name}>
                  <ComponentPin code={m.code} color="#3d6fa8" size={36} />
                </MarkerTooltip>
              </AdvancedMarker>
            ))}
            {capturedMarkers.map((m, i) => (
              <AdvancedMarker key={`c-${i}`} position={m.pos}>
                <MarkerTooltip title={m.type} subtitle={m.name}>
                  <ComponentPin code={m.code} color={m.status === 2 ? '#c8442f' : '#1d6664'} size={36} />
                </MarkerTooltip>
              </AdvancedMarker>
            ))}
            {photoMarkers.map((m, i) => (
              <AdvancedMarker key={`p-${i}`} position={m.pos}>
                <MarkerTooltip
                  title={m.exact
                    ? m.origin
                    : `${m.origin} · ${t('programaciones.visita.photoApprox')}`}
                  subtitle={m.label}
                >
                  <PhotoPin exact={m.exact} />
                </MarkerTooltip>
              </AdvancedMarker>
            ))}
            {allPositions.length > 0 && <FitBounds points={allPositions} padding={30} />}
          </KMap>

          {/* Legend overlay inside map - bottom left */}
          <Flex
            position="absolute"
            bottom={3}
            left={3}
            zIndex={999}
            bg="rgba(255,255,255,0.92)"
            backdropFilter="blur(6px)"
            px={3}
            py={2}
            borderRadius="lg"
            gap={4}
            flexWrap="wrap"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
          >
            <MapLegendItem color={P.primaryContainer} label={t('programaciones.visita.legendPolygon')} />
            <MapLegendItem color="#3d6fa8" label={t('programaciones.visita.legendDeclared')} />
            <MapLegendItem color="#1d6664" label={t('programaciones.visita.legendVerified')} />
            <MapLegendItem color="#c8442f" label={t('programaciones.visita.legendNotFound')} />
            <MapLegendItem color="#a8740e" label={t('programaciones.visita.legendRoute')} dashed />
            {photoMarkers.length > 0 && (
              <MapLegendItem color="#7C3AED" label={t('programaciones.visita.legendPhoto')} />
            )}
          </Flex>

          {/* Cuántas fotos cayeron dentro del polígono — la lectura que le
              interesa al auditor, sin obligarlo a contar pines a ojo. */}
          {photoMarkers.length > 0 && photosInsidePolygon !== null && (
            <Flex
              position="absolute"
              top={3}
              left={3}
              zIndex={999}
              bg="rgba(255,255,255,0.92)"
              backdropFilter="blur(6px)"
              px={3}
              py={2}
              borderRadius="lg"
              alignItems="center"
              gap={2}
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
            >
              <Box w="10px" h="10px" borderRadius="full" bg="#7C3AED" flexShrink={0} />
              <Text fontSize="xs" fontWeight="600" color={P.onSurface}>
                {t('programaciones.visita.photosInsidePolygon', {
                  inside: photosInsidePolygon,
                  total: photoMarkers.length,
                })}
              </Text>
            </Flex>
          )}
        </MotionBox>

        {/* ── Component Verification ── */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.22 }}
          mb={5}
        >
          <Text
            tid="programaciones.visita.componentsTitle"
            fontSize="2xl"
            fontWeight="700"
            fontFamily="Bricolage Grotesque, sans-serif"
            color={P.primary}
            mb={5}
          />

          {(!data.componentChecks || data.componentChecks.length === 0) ? (
            <Text tid="programaciones.visita.noComponentChecks" fontSize="sm" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" />
          ) : (
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={6}
            >
              {data.componentChecks.map((check) => (
                <ComponentCheckCard
                  key={check.componentCheckId}
                  check={check}
                  openAuthFile={openAuthFile}
                />
              ))}
            </Grid>
          )}
        </MotionBox>

        {/* ── Observations ── */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.26 }}
        >
          <Text
            tid="programaciones.visita.observationsTitle"
            fontSize="2xl"
            fontWeight="700"
            fontFamily="Bricolage Grotesque, sans-serif"
            color={P.primary}
            mb={5}
          />

          {(!data.observations || data.observations.length === 0) ? (
            <Text tid="programaciones.visita.noObservations" fontSize="sm" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" />
          ) : (
            <VStack gap={4} alignItems="stretch">
              {data.observations.map((obs) => (
                <ObservationCard
                  key={obs.observationId}
                  obs={obs}
                  openAuthFile={openAuthFile}
                />
              ))}
            </VStack>
          )}
        </MotionBox>

        {/* ── OCDE Audit ── */}
        {data.ocdeAudit && (
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.3 }}
            mt={5}
          >
            <OcdeAuditSection audit={data.ocdeAudit} openAuthFile={openAuthFile} />
          </MotionBox>
        )}
      </Box>

      {/* ── Review modal: comment textarea ── */}
      <Modal
        open={reviewMode !== null}
        onClose={closeReview}
        title={reviewMode === 'approve' ? 'Aprobar visita de campo' : 'Rechazar visita de campo'}
        subtitle={
          reviewMode === 'approve'
            ? 'Confirma la aprobación de esta verificación.'
            : 'Indica la razón del rechazo de esta verificación.'
        }
        size="md"
        footer={
          <>
            <Box
              as="button"
              px={4} py={2} borderRadius="lg" fontSize="sm" fontWeight="600"
              border="1px solid" borderColor="gray.200" color="#2a2622"
              cursor={submittingReview ? 'not-allowed' : 'pointer'}
              opacity={submittingReview ? 0.6 : 1}
              _hover={!submittingReview ? { bg: 'gray.50' } : undefined}
              onClick={closeReview}
              aria-disabled={submittingReview}
            >
              Cancelar
            </Box>
            <Box
              as="button"
              px={4} py={2} borderRadius="lg" fontSize="sm" fontWeight="700"
              bg={reviewMode === 'approve' ? '#1d6664' : '#9a3020'}
              color="white"
              cursor={submittingReview ? 'not-allowed' : 'pointer'}
              opacity={submittingReview ? 0.6 : 1}
              _hover={!submittingReview ? { bg: reviewMode === 'approve' ? '#174f4d' : '#991b1b' } : undefined}
              onClick={handleAskConfirm}
              aria-disabled={submittingReview}
            >
              {reviewMode === 'approve' ? 'Aprobar' : 'Rechazar'}
            </Box>
          </>
        }
      >
        <Text fontSize="sm" fontWeight="600" color="#4f4842" mb={2}>
          Comentario <Box as="span" color="#9a3020">*</Box>
        </Text>
        <Box
          as="textarea"
          value={reviewComment}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewComment(e.target.value)}
          placeholder={
            reviewMode === 'approve'
              ? 'Comentarios adicionales sobre la visita aprobada...'
              : 'Describe la razón por la cual la visita no es aceptable...'
          }
          rows={5}
          width="100%"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          fontSize="sm"
          fontFamily="'Instrument Sans', sans-serif"
          color="#2a2622"
          _focus={{ outline: 'none', borderColor: brand.green, boxShadow: `0 0 0 3px ${brand.green}33` }}
          maxLength={2000}
          disabled={submittingReview}
        />
        <Text fontSize="xs" color="#857b71" mt={2}>
          {reviewComment.trim().length}/2000 caracteres
        </Text>
      </Modal>

      {/* ── Confirmation dialog ── */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => { if (!submittingReview) setConfirmOpen(false) }}
        onConfirm={handleSubmitReview}
        title={reviewMode === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
        confirmLabel={reviewMode === 'approve' ? 'Sí, aprobar' : 'Sí, rechazar'}
        variant={reviewMode === 'approve' ? 'default' : 'danger'}
        isLoading={submittingReview}
      >
        ¿Está seguro que desea {reviewMode === 'approve' ? 'aprobar' : 'rechazar'} esta visita de campo?
        Esta acción cambiará el estado del detalle de programación y no podrá deshacerse desde la interfaz.
      </ConfirmDialog>
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// BentoField
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BentoField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text
        fontSize="10px"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="0.1em"
        color={P.onSurfaceVariant}
        fontFamily="'Instrument Sans', sans-serif"
        mb="3px"
      >
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="500" color={P.onSurface} fontFamily="'Instrument Sans', sans-serif">
        {value || '-'}
      </Text>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// MapLegendItem
// ---------------------------------------------------------------------------
function MapLegendItem({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <Flex alignItems="center" gap={2}>
      <Box
        w="14px"
        h="14px"
        borderRadius={dashed ? '0' : 'full'}
        bg={dashed ? 'transparent' : color}
        border={dashed ? `2px dashed ${color}` : 'none'}
        flexShrink={0}
      />
      <Text fontSize="xs" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif">
        {label}
      </Text>
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Hook: fetch authenticated image as blob URL
// ---------------------------------------------------------------------------
function useAuthImage(storagePath: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!storagePath) return
    let revoked = false
    const token = localStorage.getItem('auth_token')
    fetch(`${import.meta.env.VITE_API_BASE_URL}/programaciones/files/${storagePath}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !revoked) setUrl(URL.createObjectURL(blob))
      })
      .catch(() => {})
    return () => {
      revoked = true
      setUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null })
    }
  }, [storagePath])
  return url
}

// ---------------------------------------------------------------------------
// Component Check Card
// ---------------------------------------------------------------------------
function ComponentCheckCard({
  check,
  openAuthFile,
}: {
  check: VisitaComponentCheckItem
  openAuthFile: (path: string) => void
}) {
  const { t } = useT()
  const displayName = check.componentTypeName
    ? `${check.componentTypeName} (${check.componentName ?? t('programaciones.visita.dash')})`
    : check.componentName ?? t('programaciones.visita.dash')

  const statusInfo = checkStatusInfo(check.checkStatus)
  const photo = check.evidences?.find((e) => e.type === 1 && e.storagePath)
  const photoPath = useMemo(() => photo?.storagePath ?? null, [photo?.storagePath])
  const photoUrl = useAuthImage(photoPath)

  const declaredUtm = check.declaredLatitude != null && check.declaredLongitude != null
    ? latLngToUtm(check.declaredLatitude, check.declaredLongitude, 18)
    : null
  const capturedUtm = check.capturedLatitude != null && check.capturedLongitude != null
    ? latLngToUtm(check.capturedLatitude, check.capturedLongitude, 18)
    : null

  return (
    <Box
      bg={P.surfaceContainerLowest}
      border="1px solid"
      borderColor={`${P.outlineVariant}40`}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      _hover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}
      style={{ transition: 'box-shadow 0.2s ease' }}
    >
      {/* Header */}
      <Flex px={5} pt={5} pb={4} alignItems="center" gap={3}>
        <Flex
          w="48px"
          h="48px"
          borderRadius="xl"
          bg={P.surfaceContainerLow}
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Settings size={22} color={P.onSurfaceVariant} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text
            fontSize="lg"
            fontWeight="700"
            color={P.onSurface}
            fontFamily="Bricolage Grotesque, sans-serif"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {displayName}
          </Text>
          {check.componentTypeName && (
            <Text
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.08em"
              color={P.onSurfaceVariant}
              fontFamily="'Instrument Sans', sans-serif"
            >
              {check.componentTypeName}
            </Text>
          )}
        </Box>
        <Box
          px={3}
          py="4px"
          borderRadius="full"
          fontSize="10px"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.06em"
          bg={statusInfo.bg}
          color={statusInfo.color}
          fontFamily="'Instrument Sans', sans-serif"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          {t(statusInfo.labelTid)}
        </Box>
      </Flex>

      {/* Coordinates: two separate rounded boxes */}
      <Grid templateColumns="1fr 1fr" gap={3} mx={5} mb={4}>
        <Box
          bg={P.surfaceContainerLow}
          borderRadius="lg"
          px={5}
          py={4}
        >
          <Text
            tid="programaciones.visita.coordDeclared"
            fontSize="11px"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.08em"
            color={P.onSurfaceVariant}
            fontFamily="'Instrument Sans', sans-serif"
            mb={3}
          />
          <Text fontSize="15px" fontFamily="'Courier New', monospace" fontWeight="600" color={P.onSurface} lineHeight={1.4}>
            {declaredUtm ? t('programaciones.visita.coordEasting', { value: declaredUtm.easting.toLocaleString('en') }) : t('programaciones.visita.dash')}
          </Text>
          <Text fontSize="15px" fontFamily="'Courier New', monospace" fontWeight="600" color={P.onSurface} lineHeight={1.4}>
            {declaredUtm ? t('programaciones.visita.coordNorthing', { value: declaredUtm.northing.toLocaleString('en') }) : ''}
          </Text>
        </Box>
        <Box
          bg={P.surfaceContainerLow}
          borderRadius="lg"
          px={5}
          py={4}
        >
          <Text
            tid="programaciones.visita.coordCaptured"
            fontSize="11px"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.08em"
            color={P.onSurfaceVariant}
            fontFamily="'Instrument Sans', sans-serif"
            mb={3}
          />
          <Text fontSize="15px" fontFamily="'Courier New', monospace" fontWeight="600" color={P.onSurface} lineHeight={1.4}>
            {capturedUtm ? t('programaciones.visita.coordEasting', { value: capturedUtm.easting.toLocaleString('en') }) : t('programaciones.visita.dash')}
          </Text>
          <Text fontSize="15px" fontFamily="'Courier New', monospace" fontWeight="600" color={P.onSurface} lineHeight={1.4}>
            {capturedUtm ? t('programaciones.visita.coordNorthing', { value: capturedUtm.northing.toLocaleString('en') }) : ''}
          </Text>
        </Box>
      </Grid>

      {/* Precision bar */}
      {check.distanceMeters != null && (
        <Flex
          mx={5}
          mb={4}
          px={4}
          py={3}
          borderRadius="lg"
          alignItems="center"
          gap={2}
          bg={check.isWithinMargin ? `${P.primaryFixed}4D` : 'rgba(200, 68, 47,0.1)'}
        >
          <Ruler size={14} color={P.onSurfaceVariant} style={{ transform: 'rotate(-45deg)' }} />
          <Text tid="programaciones.visita.precisionLabel" values={{ value: check.distanceMeters.toFixed(1) }} fontSize="sm" fontWeight="600" color={P.onSurface} fontFamily="'Instrument Sans', sans-serif" />
          <Text
            ml="auto"
            fontSize="10px"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.04em"
            fontFamily="'Instrument Sans', sans-serif"
            color={check.isWithinMargin ? P.onPrimaryFixedVariant : '#c8442f'}
          >
            {check.isWithinMargin ? t('programaciones.visita.withinMargin') : t('programaciones.visita.outsideMargin')}
          </Text>
        </Flex>
      )}

      {/* Evidence photo – full-width flush with card bottom */}
      {photo && photo.storagePath ? (
        <Box
          h="200px"
          bg={P.surfaceContainerLow}
          overflow="hidden"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ opacity: 0.9 }}
          style={{ transition: 'opacity 0.15s' }}
          onClick={() => openAuthFile(photo.storagePath!)}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={t('programaciones.visita.evidenceAlt')}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <VStack gap={1}>
              <Spinner size="sm" color={P.onSurfaceVariant} />
              <Text tid="programaciones.visita.loadingPhoto" fontSize="xs" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" />
            </VStack>
          )}
        </Box>
      ) : (
        <Box
          h="200px"
          bg={P.surfaceContainerLow}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack gap={1}>
            <Camera size={24} color={`${P.onSurfaceVariant}60`} />
            <Text tid="programaciones.visita.evidencePhotoLabel" fontSize="xs" color={`${P.onSurfaceVariant}80`} fontFamily="'Instrument Sans', sans-serif" />
          </VStack>
        </Box>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Observation Card
// ---------------------------------------------------------------------------
function ObservationCard({
  obs,
  openAuthFile,
}: {
  obs: VisitaObservationItem
  openAuthFile: (path: string) => void
}) {
  return (
    <Box
      bg={P.surfaceContainerLowest}
      border="1px solid"
      borderColor={`${P.outlineVariant}40`}
      borderRadius="xl"
      p={5}
    >
      {/* Header row */}
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap" mb={3}>
        <Flex alignItems="center" gap={3} flex={1} flexWrap="wrap">
          {obs.observationReasonName && (
            <Box
              px={3}
              py="4px"
              borderRadius="full"
              fontSize="11px"
              fontWeight="700"
              bg="#f9eccf"
              color="#92400E"
              fontFamily="'Instrument Sans', sans-serif"
            >
              {obs.observationReasonName}
            </Box>
          )}
          {obs.photoFileName && (
            <Flex
              alignItems="center"
              gap={1.5}
              color="#1d6664"
              cursor={obs.photoStoragePath ? 'pointer' : 'default'}
              fontFamily="'Instrument Sans', sans-serif"
              fontSize="xs"
              fontWeight="600"
              onClick={() => obs.photoStoragePath && openAuthFile(obs.photoStoragePath)}
              _hover={obs.photoStoragePath ? { textDecoration: 'underline' } : {}}
            >
              <Camera size={13} />
              <Text>{obs.photoFileName}</Text>
            </Flex>
          )}
        </Flex>
        <Text fontSize="xs" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" whiteSpace="nowrap">
          {formatDatetime(obs.mobileTimestamp ?? obs.createdAt)}
        </Text>
      </Flex>

      {/* Comments */}
      {obs.comments && (
        <Text fontSize="sm" color={P.onSurface} fontFamily="'Instrument Sans', sans-serif" mb={3} lineHeight={1.7}>
          {obs.comments}
        </Text>
      )}

      {/* GPS coordinates */}
      {obs.latitude != null && obs.longitude != null && (
        <Flex alignItems="center" gap={1.5} color={P.onSurfaceVariant}>
          <Navigation size={12} />
          <Text fontSize="xs" fontFamily="monospace" fontWeight="500">
            {obs.latitude.toFixed(6)}, {obs.longitude.toFixed(6)}
          </Text>
        </Flex>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// OCDE Audit
// ---------------------------------------------------------------------------
function OcdeAuditSection({
  audit,
  openAuthFile,
}: {
  audit: VisitaOcdeAuditResult
  openAuthFile: (path: string) => void
}) {
  const pct = audit.totalQuestions > 0
    ? Math.round((audit.answeredQuestions / audit.totalQuestions) * 100)
    : 0
  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" gap={3} flexWrap="wrap" mb={5}>
        <Text fontSize="2xl" fontWeight="700" fontFamily="Bricolage Grotesque, sans-serif" color={P.primary}>
          Cumplimiento Normativo
        </Text>
        <Flex alignItems="center" gap={2} flexWrap="wrap">
          {audit.required && (
            <Box px={3} py="4px" borderRadius="full" fontSize="11px" fontWeight="700" bg="#f9eccf" color="#92400E" fontFamily="'Instrument Sans', sans-serif">
              Obligatorio
            </Box>
          )}
          <Box
            px={3}
            py="4px"
            borderRadius="full"
            fontSize="12px"
            fontWeight="700"
            bg={pct === 100 ? 'rgba(21,128,61,0.12)' : `${P.outlineVariant}33`}
            color={pct === 100 ? '#1d6664' : P.onSurfaceVariant}
            fontFamily="'Instrument Sans', sans-serif"
          >
            {audit.answeredQuestions} de {audit.totalQuestions} atendidas ({pct}%)
          </Box>
        </Flex>
      </Flex>

      <VStack gap={3} alignItems="stretch">
        {audit.categories.map((cat) => (
          <OcdeCategoryCard key={cat.categoryId} category={cat} openAuthFile={openAuthFile} />
        ))}
      </VStack>
    </>
  )
}

function OcdeCategoryCard({
  category,
  openAuthFile,
}: {
  category: VisitaOcdeCategoryItem
  openAuthFile: (path: string) => void
}) {
  const [open, setOpen] = useState(false)
  const total = category.questions.length
  const answered = category.questions.filter((q) => q.answer !== null).length
  const complete = total > 0 && answered === total

  return (
    <Box
      bg={P.surfaceContainerLowest}
      border="1px solid"
      borderColor={`${P.outlineVariant}40`}
      borderRadius="xl"
      overflow="hidden"
    >
      <Flex
        role="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        alignItems="center"
        justifyContent="space-between"
        gap={3}
        p={4}
        cursor="pointer"
        _hover={{ bg: P.surfaceContainerLow }}
      >
        <Flex alignItems="center" gap={2} flex={1} minW={0}>
          <Box
            fontSize="13px"
            fontWeight="700"
            color={P.onSurfaceVariant}
            transform={open ? 'rotate(90deg)' : 'rotate(0deg)'}
            lineHeight={1}
          >
            ▸
          </Box>
          <Text fontSize="sm" fontWeight="700" color={P.onSurface} fontFamily="'Instrument Sans', sans-serif">
            {category.code}. {category.name}
          </Text>
        </Flex>
        <Box
          px={2.5}
          py="3px"
          borderRadius="full"
          fontSize="11px"
          fontWeight="700"
          bg={complete ? 'rgba(21,128,61,0.12)' : `${P.outlineVariant}33`}
          color={complete ? '#1d6664' : P.onSurfaceVariant}
          fontFamily="'Instrument Sans', sans-serif"
          flexShrink={0}
        >
          {answered}/{total}
        </Box>
      </Flex>

      {open && (
        <Box px={4} pb={3}>
          {category.questions.map((q) => (
            <OcdeQuestionRow key={q.questionId} q={q} openAuthFile={openAuthFile} />
          ))}
        </Box>
      )}
    </Box>
  )
}

function OcdeQuestionRow({
  q,
  openAuthFile,
}: {
  q: VisitaOcdeQuestionItem
  openAuthFile: (path: string) => void
}) {
  const photoUrl = useAuthImage(q.photoStoragePath)

  const answerStyle = q.answer === true
    ? { label: 'Sí', bg: 'rgba(21,128,61,0.12)', color: '#1d6664', Icon: CircleCheck }
    : q.answer === false
      ? { label: 'No', bg: 'rgba(185,28,28,0.12)', color: '#9a3020', Icon: CircleX }
      : { label: 'Sin responder', bg: `${P.outlineVariant}33`, color: P.onSurfaceVariant, Icon: null }
  const AnswerIcon = answerStyle.Icon

  return (
    <Box py={3} borderTop="1px solid" borderColor={`${P.outlineVariant}30`}>
      <Flex justifyContent="space-between" alignItems="flex-start" gap={3}>
        <Text fontSize="sm" color={P.onSurface} fontFamily="'Instrument Sans', sans-serif" lineHeight={1.6}>
          <Box as="span" fontWeight="700" color={P.onSurfaceVariant} mr={1.5}>{q.code}</Box>
          {q.text}
        </Text>
        <Flex
          alignItems="center"
          gap={1.5}
          px={2.5}
          py="3px"
          borderRadius="full"
          bg={answerStyle.bg}
          flexShrink={0}
        >
          {AnswerIcon && <AnswerIcon size={12} color={answerStyle.color} />}
          <Text fontSize="11px" fontWeight="700" color={answerStyle.color} fontFamily="'Instrument Sans', sans-serif" whiteSpace="nowrap">
            {answerStyle.label}
          </Text>
        </Flex>
      </Flex>

      {q.comments && (
        <Text fontSize="xs" color={P.onSurfaceVariant} mt={2} lineHeight={1.6} fontFamily="'Instrument Sans', sans-serif">
          {q.comments}
        </Text>
      )}

      {q.photoStoragePath && (
        <Box mt={2}>
          <Box
            maxW="240px"
            h="150px"
            bg={P.surfaceContainerLow}
            borderRadius="lg"
            overflow="hidden"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={() => q.photoStoragePath && openAuthFile(q.photoStoragePath)}
          >
            {photoUrl ? (
              <img src={photoUrl} alt={q.photoFileName ?? 'Evidencia OCDE'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <VStack gap={1}>
                <Camera size={20} color={`${P.onSurfaceVariant}80`} />
                <Text fontSize="11px" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif">Ver foto</Text>
              </VStack>
            )}
          </Box>
          {q.photoLatitude != null && q.photoLongitude != null && (
            <Flex alignItems="center" gap={1.5} color={P.onSurfaceVariant} mt={1}>
              <Navigation size={11} />
              <Text fontSize="11px" fontFamily="monospace" fontWeight="500">
                {q.photoLatitude.toFixed(6)}, {q.photoLongitude.toFixed(6)}
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Timeline Entry
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TimelineEntry({ log, isLast }: { log: VisitaStateLogItem; isLast: boolean }) {
  const { t } = useT()
  const actionColor = stateLogColor(log.actionType)
  const newStatusLabel = detailStatusLabel(log.newStatus)
  const detStatusStyle = detailStatusColors[log.newStatus] ?? { bg: 'rgba(181, 170, 157,0.2)', color: '#857b71' }

  return (
    <Flex gap={4} pb={isLast ? 0 : 5} position="relative">
      {/* Dot */}
      <Flex
        w="24px"
        h="24px"
        borderRadius="full"
        bg={actionColor}
        color="white"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        zIndex={1}
        position="relative"
      >
        {stateLogIcon(log.actionType)}
      </Flex>

      {/* Content */}
      <Box flex={1} minW={0} pt="3px">
        <Flex alignItems="center" gap={2} flexWrap="wrap" mb={0.5}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color={P.onSurface}
            fontFamily="Bricolage Grotesque, sans-serif"
            textTransform="capitalize"
          >
            {log.actionType ?? t('programaciones.visita.actionGeneric')}
          </Text>
          <Box
            px={2}
            py="2px"
            borderRadius="full"
            fontSize="10px"
            fontWeight="700"
            fontFamily="'Instrument Sans', sans-serif"
            bg={detStatusStyle.bg}
            color={detStatusStyle.color}
          >
            {newStatusLabel}
          </Box>
        </Flex>

        {log.pauseReasonText && (
          <Text tid="programaciones.visita.motiveLabel" values={{ reason: log.pauseReasonText }} fontSize="xs" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" mb={0.5} />
        )}

        <Flex gap={3} fontSize="xs" color={P.onSurfaceVariant} fontFamily="'Instrument Sans', sans-serif" flexWrap="wrap">
          <Text>{formatDatetime(log.mobileTimestamp ?? log.createdAt)}</Text>
          {log.latitude != null && log.longitude != null && (
            <Flex alignItems="center" gap={1}>
              <Navigation size={10} />
              <Text fontFamily="monospace">
                {log.latitude.toFixed(6)}, {log.longitude.toFixed(6)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Box>
    </Flex>
  )
}
